import { backUrl } from "../Api/Urls";
import { ServiceConfig } from "../types/unified-services";

// Body Measurement Service Configuration
export const BODY_MEASUREMENT_CONFIG: ServiceConfig = {
  serviceId: "body-measurement",
  serviceName: "Body Measurement",

  guide: {
    slides: [
      {
        id: "stand-straight",
        title: "Stand Straight",
        description:
          "Before taking a photo, pay attention to the following points:\n\nPlace the camera on the table at 90 degrees\nStand straight in front of the camera\nCapture your complete height",
        image: "/images/guide/stand-straight.png",
      },
      {
        id: "front-pose",
        title: "Front Pose",
        description: "Before taking a photo, pay attention to the following points:\n\nLet your hands hang by your sides\nDon't wear baggy clothes\nDon't hide your body shape in clothes",
        image: "/images/guide/front-pose.png",
      },
      {
        id: "side-pose",
        title: "Side Pose",
        description: "Before taking a photo, pay attention to the following points:\n\nDo not stand at an angle\nHang your arms on your thighs' side",
        image: "/images/guide/side-pose.png",
      },
      {
        id: "privacy-consent",
        title: "Privacy & Security",
        description:
          "To ensure your privacy and security, we need your consent to access your camera and process your images. All images will be processed locally on your device and any face detection will be blurred before sending data to our servers. Do you agree to these terms?",
        image: "/images/guide/privacy.png",
      },
      // {
      //   id: "front-instruction",
      //   title: "Front Position",
      //   description: "Like the picture above, stand in front of the camera with your front side.",
      //   image: "/images/guide/full_3.webp",
      // },
    ],
    showPagination: true,
  },

  configuration: {
    requiresHeight: true,
    heightUnits: ["cm", "inch"],
    heightValidation: {
      minCm: 51,
      maxCm: 230,
    },
  },

  upload: {
    photoCount: 2,
    photoLabels: ["Front View", "Side View"],
    photoDescriptions: [
      {
        title: "For a Front Side Image",
        instructions: [
          "Ensure the full body is visible.",
          "Stand directly facing the camera.",
          "Stand straight; don't slouch.",
          "Wear fitted clothing, not loose.",
          "Keep arms slightly apart from the body.",
          "Ensure no shoes are included in the image.",
          "Keep each image under 3MB.",
        ],
      },
      {
        title: "For a Side Image",
        instructions: [
          "Ensure the full body is visible.",
          "Stand in full profile (side view).",
          "Stand straight; don't slouch.",
          "Wear fitted clothing, not loose.",
          "Ensure no shoes are included in the image.",
          "Keep each image under 3MB.",
        ],
      },
    ],
    placeholderImages: ["/images/guide/front-pose.png", "/images/guide/side-pose.png"],
  },

  camera: {
    enabled: true,
    photoCount: 2,
    requiresOrientation: true,
    requiresMicrophone: false,
    voiceGuidance: true,
  },
  processing: {
    requiresFaceBlur: true,
    imageValidation: {
      maxSizeKB: 3072, // 3MB
      allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/webp"],
      minWidth: 300,
      minHeight: 300,
    },
  },

  api: {
    endpoint: `${backUrl}/api/v1/services/bodyMeasurement`,

    method: "POST",
    requiresAuth: true,
  },

  results: {
    template: "body-measurement",
    allowExport: true,
    allowSave: true,
    unitConversion: true,
  },
};

// Auto Tagging Service Configuration
export const AUTO_TAGGING_CONFIG: ServiceConfig = {
  serviceId: "auto-tagging",
  serviceName: "Auto Tagging",

  guide: {
    slides: [
      {
        id: "intro",
        title: "Auto Tagging",
        description:
          "Shaku's Auto Tagging feature transforms online apparel cataloging by automatically identifying and tagging key clothing attributes from images. This streamlines inventory management, improves search functionality, and enhances customer engagement, ultimately driving better sales.",
      },
      {
        id: "upload-requirements",
        title: "Upload Requirements",
        description: "Please check the following details before uploading your clothes image:",
        list: [
          "Ensure the front view of the clothing is fully visible",
          "Include the entire item within the frame",
          "Avoid shadows on the clothing for accurate color representation",
          "Make sure sleeves and collars are clearly shown",
          "Gender based on wearer of clothing",
          "Keep each image under 3MB",
        ],
      },
    ],
    showPagination: true,
  },

  upload: {
    photoCount: 1,
    photoLabels: ["Clothing Image"],
    photoDescriptions: [
      {
        title: "Upload Requirements",
        instructions: [
          "Ensure the front view of the clothing is fully visible.",
          "Include the entire item within the frame.",
          "Avoid shadows on the clothing for accurate color representation.",
          "Make sure sleeves and collars are clearly shown.",
          "Gender based on wearer of clothing.",
          "Keep each image under 3MB.",
        ],
      },
    ],
    placeholderImages: ["/images/default/default-auto-tagging.png"],
  },

  processing: {
    requiresFaceBlur: false,
    imageValidation: {
      maxSizeKB: 3072, // 3MB
      allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/webp"],
      minWidth: 300,
      minHeight: 300,
    },
  },

  api: {
    endpoint: `${backUrl}/api/v1/services/autoTagging`,

    method: "POST",
    requiresAuth: true,
  },
  results: {
    template: "auto-tagging",
    allowExport: true,
    allowSave: false,
    unitConversion: false,
  },
};

// Visual Clothes Search Service Configuration
export const VISUAL_CLOTHES_SEARCH_CONFIG: ServiceConfig = {
  serviceId: "visual-search",
  serviceName: "Visual Clothes Search",

  guide: {
    slides: [
      {
        id: "intro",
        title: "Visual Clothes Search",
        description: "Find similar clothing items from your photos using AI-powered visual recognition",
        image: "/images/guide/side_2.webp",
      },
      {
        id: "process",
        title: "How it works",
        description: "Upload a photo of clothing and we'll analyze it to find similar items available online from various retailers",
        image: "/images/guide/full.webp",
      },
      {
        id: "tips",
        title: "Best Results",
        description: "For best results, ensure the clothing is clearly visible and well-lit in your photo",
        image: "/images/guide/full_2.webp",
      },
    ],
    showPagination: true,
  },

  upload: {
    photoCount: 1,
    photoLabels: ["Clothing Image"],
    photoDescriptions: [
      {
        title: "For Best Results",
        instructions: [
          "Ensure the front view of the clothing is fully visible.",
          "Include the entire item within the frame.",
          "Avoid shadows on the clothing for accurate color representation.",
          "Make sure sleeves and collars are clearly shown.",
          "Limit image size to a maximum of 5MB.",
          "Use good lighting for better recognition.",
        ],
      },
    ],
    placeholderImages: ["/assets/image/free-trial/visual-search/temp-visual-search.png"],
  },

  camera: {
    enabled: true,
    photoCount: 1,
    requiresOrientation: false,
    requiresMicrophone: false,
    voiceGuidance: false,
  },

  processing: {
    requiresFaceBlur: false,
    imageValidation: {
      maxSizeKB: 3072, // 3MB
      allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/webp"],
      minWidth: 300,
      minHeight: 300,
    },
  },

  api: {
    endpoint: `${backUrl}/api/v1/services/search/clothesRecognition`,

    method: "POST",
    requiresAuth: true,
  },

  results: {
    template: "visual-search",
    allowExport: false,
    allowSave: true,
    unitConversion: false,
  },
};

// Garment Measurement Service Configuration
export const GARMENT_MEASUREMENT_CONFIG: ServiceConfig = {
  serviceId: "garment-measurement",
  serviceName: "Garment Measurement",
  guide: {
    slides: [
      {
        id: "intro",
        title: "Garment Measurement",
        description:
          "Shaku's Garment Measurement service provides precise measurements of clothing items from uploaded images. This technology helps shoppers ensure perfect fit by comparing garment dimensions with their preferred sizes, reducing returns and enhancing customer satisfaction.",
      },
      {
        id: "upload-instructions",
        title: "Upload Instructions",
        description: "Make sure to follow these instructions when uploading your garment images:",
        list: [
          "Ensure the garment is a T-shirt",
          "Capture the entire front of the T-shirt in portrait orientation (no tilted or rotated images)",
          "Place a flat A4 sheet next to the T-shirt for scale (not on top) and ensure it has no folds or wrinkles",
          "Make sure all corners of both the T-shirt and the A4 sheet are fully visible",
        ],
      },
      {
        id: "additional-tips",
        title: "Additional Requirements",
        description: "Additional guidelines for optimal results:",
        list: ["Use a flat surface with a plain, uncluttered background", "Avoid shadows for accurate measurements", "Keep each image under 3MB", "You can upload up to 50 images"],
      },
    ],
    showPagination: true,
  },

  upload: {
    photoCount: "multiple", // Special flag for multi-image upload
    maxImages: 50,
    photoLabels: ["Garment Images"],
    photoDescriptions: [
      {
        title: "Garment Upload Instructions",
        instructions: [
          "Ensure the garment is a T-shirt",
          "Capture the entire front of the T-shirt in portrait orientation (no tilted or rotated images)",
          "Place a flat A4 sheet next to the T-shirt for scale (not on top) and ensure it has no folds or wrinkles",
          "Make sure all corners of both the T-shirt and the A4 sheet are fully visible",
          "Use a flat surface with a plain, uncluttered background",
          "Avoid shadows for accurate measurements",
          "Keep each image under 3MB",
          "You can upload up to 50 images",
        ],
      },
    ],
    placeholderImages: ["/images/default/default-garment.png"],
  },

  camera: {
    enabled: false, // Disable camera for garment measurement
    photoCount: 1,
    requiresOrientation: false,
    requiresMicrophone: false,
    voiceGuidance: false,
  },

  processing: {
    requiresFaceBlur: false,
    imageValidation: {
      maxSizeKB: 3072, // 3MB
      allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/webp"],
      minWidth: 300,
      minHeight: 300,
    },
  },
  api: {
    endpoint: `${backUrl}/api/v1/services/garmentMeasurement`,

    method: "POST",
    requiresAuth: true,
  },

  results: {
    template: "garment-measurement",
    allowExport: true,
    allowSave: true,
    unitConversion: true,
  },
};

// Service Registry
export const SERVICE_CONFIGS = {
  "body-measurement": BODY_MEASUREMENT_CONFIG,
  "auto-tagging": AUTO_TAGGING_CONFIG,
  "visual-search": VISUAL_CLOTHES_SEARCH_CONFIG,
  "garment-measurement": GARMENT_MEASUREMENT_CONFIG,
} as const;

export type ServiceId = keyof typeof SERVICE_CONFIGS;

// Demo Service Configurations (Same as regular but requiresAuth: false)
export const DEMO_BODY_MEASUREMENT_CONFIG: ServiceConfig = {
  ...BODY_MEASUREMENT_CONFIG,
  api: {
    ...BODY_MEASUREMENT_CONFIG.api,
    endpoint: `${backUrl}/api/v1/demo/services/bodyMeasurement`,

    requiresAuth: false,
  },
};

export const DEMO_AUTO_TAGGING_CONFIG: ServiceConfig = {
  ...AUTO_TAGGING_CONFIG,
  api: {
    ...AUTO_TAGGING_CONFIG.api,
    endpoint: `${backUrl}/api/v1/demo/services/autoTagging`,

    requiresAuth: false,
  },
};

export const DEMO_VISUAL_CLOTHES_SEARCH_CONFIG: ServiceConfig = {
  ...VISUAL_CLOTHES_SEARCH_CONFIG,
  api: {
    ...VISUAL_CLOTHES_SEARCH_CONFIG.api,
    endpoint: `${backUrl}/api/v1/demo/services/search/clothesRecognition`,

    requiresAuth: false,
  },
};

export const DEMO_GARMENT_MEASUREMENT_CONFIG: ServiceConfig = {
  ...GARMENT_MEASUREMENT_CONFIG,
  api: {
    ...GARMENT_MEASUREMENT_CONFIG.api,
    endpoint: `${backUrl}/api/v1/demo/services/garmentMeasurement`,

    requiresAuth: false,
  },
};

// Helper function to get service config
export const getServiceConfig = (serviceId: ServiceId): ServiceConfig => {
  const config = SERVICE_CONFIGS[serviceId];
  if (!config) {
    throw new Error(`Service configuration not found for: ${serviceId}`);
  }
  return config;
};
